import { useNavigate } from 'react-router-dom';
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  APPROVAL_STAGE_LABEL,
  applicantEmail,
  approverFor,
  companiesHouseUrl,
  companyCheckSummary,
  companyChecksOf,
  companyStaffWarning,
  customerFields,
  enabledCountries,
  iqHint,
  iqHintAnswer,
  isActionable,
  isContactMetaKey,
  primaryContactIndex,
  resolveRepeaterSubFields,
  staffFields,
  stageStaffFields,
  type ApprovalStage,
  type ApprovalTiming,
  type CompanyCheck,
  type Country,
  type FieldDef,
} from '@jfh/intake-shared';
import { api, ApiRequestError, type ActivityRow, type SubmissionDetailResponse } from '../lib/api.js';
import { Icon } from '../components/Icon.js';
import { Avatar, EmptyState } from '../components/Primitives.js';
import { useToast } from '../components/Toast.js';
import { useIsAdmin } from '../lib/useIsAdmin.js';
import { ApplicationPill, shortId } from './badges.js';
import { activityLine, applicationType, teamName } from './wording.js';
import { StageStepper } from './StageStepper.js';
import { StaffFields, seedStaffValues } from './StaffFields.js';
import { ApplicantEditor, isApplicantEditable } from './ApplicantEditor.js';
import { DeclineModal } from './DeclineModal.js';
import { CardPreviewModal } from './CardPreview.js';
import { MoreMenu, type MoreItem } from './MoreMenu.js';
import {
  answerGroups,
  approveConsequence,
  approveLabel,
  approvedToast,
  declineDialog,
  declinedToast,
  decidedSentence,
} from './detail.js';
import { displayValue, fmtTime, isRepeaterValue, timeAgo, ukDayTime, valueFields } from './fieldDisplay.js';

function SectionLabel({ icon, title, hint }: { icon: string; title: string; hint?: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 9 }}>
      <Icon name={icon} size={16} style={{ color: 'var(--text-3)', flex: 'none' }} />
      <span style={{ fontWeight: 700, fontSize: 13.5, whiteSpace: 'nowrap', flex: 'none' }}>
        {title}
      </span>
      {hint && (
        <span
          style={{
            fontSize: 11.5,
            color: 'var(--text-3)',
            marginLeft: 'auto',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            minWidth: 0,
          }}
        >
          {hint}
        </span>
      )}
    </div>
  );
}

function ActivityTrail({ rows }: { rows: ActivityRow[] }) {
  if (rows.length === 0) {
    return <div style={{ fontSize: 12.5, color: 'var(--text-3)', marginTop: 12 }}>Nothing yet.</div>;
  }
  return (
    <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column' }}>
      {rows.map((h, i) => {
        const line = activityLine(h);
        const bad = line.tone === 'bad';
        const ok = line.tone === 'ok';
        return (
          <div key={h.id} style={{ display: 'flex', gap: 12, paddingBottom: i < rows.length - 1 ? 16 : 0 }}>
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
              <div
                style={{
                  width: 22,
                  height: 22,
                  borderRadius: 99,
                  flex: 'none',
                  background: bad ? 'var(--st-declined-soft)' : ok ? 'var(--accent-soft)' : 'var(--bg-sunken)',
                  color: bad ? 'var(--st-declined)' : ok ? 'var(--accent-text)' : 'var(--text-3)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                <Icon name={line.icon} size={13} />
              </div>
              {i < rows.length - 1 && (
                <div style={{ width: 2, flex: 1, background: 'var(--border)', marginTop: 2 }} />
              )}
            </div>
            <div style={{ flex: 1, paddingTop: 1 }}>
              <div style={{ fontSize: 13, fontWeight: 600 }}>{line.text}</div>
              <div style={{ fontSize: 11.5, color: 'var(--text-3)' }}>
                {h.actor || 'System'} · {fmtTime(h.created_at)}
              </div>
              {line.note && (
                <div
                  style={{
                    fontSize: 12.5,
                    color: 'var(--text-2)',
                    marginTop: 4,
                    padding: '7px 10px',
                    background: 'var(--bg-sunken)',
                    borderRadius: 7,
                  }}
                >
                  {line.note}
                </div>
              )}
            </div>
          </div>
        );
      })}
    </div>
  );
}

function FieldRow({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 14,
        padding: '9px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600 }}>{label}</div>
      <div style={{ fontSize: 13.5, color: value ? 'var(--text-1)' : 'var(--text-3)', overflowWrap: 'anywhere' }}>
        {value || 'Not answered'}
      </div>
    </div>
  );
}

/**
 * A company number (F8) with what Companies House said at submit: status, type,
 * incorporation, nature of business — or a warning when it couldn't be checked.
 */
function CompanyRows({ label, value, check }: { label: string; value: string; check?: CompanyCheck }) {
  const unchecked = Boolean(value) && check?.outcome !== 'verified';
  const warning = value ? companyStaffWarning(check) : null;
  const p = check?.profile;
  return (
    <>
      <FieldRow label={label} value={value} />
      {value && (
        <div
          style={{
            margin: '8px 0 4px',
            padding: '10px 12px',
            borderRadius: 8,
            fontSize: 12.5,
            lineHeight: 1.5,
            background: warning ? 'var(--danger-100)' : unchecked ? 'var(--warning-100)' : 'var(--bg-surface-2)',
            color: 'var(--text-1)',
          }}
        >
          {warning && <div style={{ fontWeight: 800, color: 'var(--st-declined)' }}>{warning}</div>}
          <div style={{ fontWeight: 700 }}>Companies House: {companyCheckSummary(check)}</div>
          {p && (
            <>
              <div>{p.name}</div>
              {p.sicCodes.length > 0 && <div>Nature of business (SIC): {p.sicCodes.join(', ')}</div>}
            </>
          )}
          <a
            href={companiesHouseUrl(value)}
            target="_blank"
            rel="noreferrer"
            style={{ fontWeight: 600, color: 'var(--accent-text)' }}
          >
            View on Companies House →
          </a>
        </div>
      )}
    </>
  );
}

/**
 * Render a repeater field's value (an array of entry objects) as labelled
 * sub-blocks — "Contact 1", "Contact 2" — with each sub-field's `label: value`.
 * Sub-field labels come from the field schema where available, else the raw key.
 */
function RepeaterRows({ field, items }: { field: FieldDef; items: Record<string, unknown>[] }) {
  const subs = resolveRepeaterSubFields(field);
  const labelFor = (key: string) => subs.find((s) => s.id === key)?.label ?? key;
  const itemLabel = field.itemLabel?.trim() || 'Item';
  const primaryIdx = field.type === 'contact' ? primaryContactIndex(items) : -1;

  return (
    <div
      style={{
        display: 'grid',
        gridTemplateColumns: '160px 1fr',
        gap: 14,
        padding: '9px 0',
        borderBottom: '1px solid var(--border)',
      }}
    >
      <div style={{ fontSize: 12.5, color: 'var(--text-2)', fontWeight: 600 }}>{field.label}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {items.map((item, i) => {
          const pairs = Object.entries(item)
            .filter(([k]) => !isContactMetaKey(k))
            .map(([k, v]) => [labelFor(k), displayValue(v)] as const)
            .filter(([, v]) => v);
          return (
            <div key={i}>
              <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)', marginBottom: 3 }}>
                {itemLabel} {i + 1}
                {i === primaryIdx && (
                  <span
                    style={{
                      marginLeft: 8,
                      padding: '1px 7px',
                      borderRadius: 99,
                      fontSize: 10.5,
                      fontWeight: 700,
                      background: 'var(--accent-soft)',
                      color: 'var(--accent-text)',
                    }}
                  >
                    Primary
                  </span>
                )}
              </div>
              {pairs.length === 0 ? (
                <div style={{ fontSize: 13, color: 'var(--text-3)' }}>—</div>
              ) : (
                <div style={{ display: 'flex', flexDirection: 'column', gap: 2, paddingLeft: 10 }}>
                  {pairs.map(([k, v]) => (
                    <div key={k} style={{ fontSize: 13, color: 'var(--text-1)' }}>
                      <span style={{ color: 'var(--text-2)' }}>{k}: </span>
                      {v}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


const isBlank = (v: unknown) => v === undefined || v === null || (typeof v === 'string' && v.trim() === '');

/** A notice for something that went wrong, with its one fix button (F10 P3). */
function ProblemNotice({
  title,
  body,
  said,
  button,
  busy,
  onFix,
}: {
  title: string;
  body: string;
  said: string | null;
  button: { icon: string; label: string; busyLabel: string };
  busy: boolean;
  onFix: () => void;
}) {
  return (
    <div
      className="card"
      role="status"
      style={{ padding: '14px 18px', marginBottom: 18, borderLeft: '3px solid var(--st-declined)' }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12, flexWrap: 'wrap' }}>
        <div style={{ minWidth: 0, flex: 1 }}>
          <div style={{ fontSize: 13.5 }}>
            <strong>{title}</strong> {body}
          </div>
          {said && (
            <div style={{ fontSize: 12, color: 'var(--text-3)', marginTop: 4, whiteSpace: 'pre-wrap', overflowWrap: 'anywhere' }}>
              {said}
            </div>
          )}
        </div>
        <button className="btn sm" style={{ flex: 'none' }} onClick={onFix} disabled={busy}>
          <Icon name={button.icon} size={14} /> {busy ? button.busyLabel : button.label}
        </button>
      </div>
    </div>
  );
}

export function SubmissionDetail({ id, onChanged }: { id: string; onChanged: () => void }) {
  const [data, setData] = useState<SubmissionDetailResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [staffValues, setStaffValues] = useState<Record<string, string>>({});
  const [missing, setMissing] = useState<ReadonlySet<string>>(new Set());
  const [declineOpen, setDeclineOpen] = useState(false);
  const [cardOpen, setCardOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [resending, setResending] = useState(false);
  const [resendingIq, setResendingIq] = useState(false);
  const [editing, setEditing] = useState(false);
  const [edits, setEdits] = useState<Record<string, unknown>>({});
  const [savingEdit, setSavingEdit] = useState(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [defaultCountryIso, setDefaultCountryIso] = useState('GB');
  const staffRef = useRef<HTMLDivElement>(null);
  const toast = useToast();
  const isAdmin = useIsAdmin();
  const navigate = useNavigate();

  useEffect(() => {
    api
      .getCountryConfig()
      .then((c) => {
        setCountries(enabledCountries(c.disabled));
        setDefaultCountryIso(c.default || 'GB');
      })
      .catch(() => undefined);
  }, []);

  const show = useCallback((d: SubmissionDetailResponse) => {
    setData(d);
    setStaffValues(
      seedStaffValues(staffFields(d.schema), {
        ...d.submission.customerData,
        ...d.submission.internalData,
      }),
    );
    setMissing(new Set());
    setError(null);
  }, []);

  const load = useCallback(
    () =>
      api
        .getSubmission(id)
        .then((d) => {
          show(d);
          return d;
        })
        .catch((e: unknown) => {
          setError(e instanceof Error ? e.message : 'Couldn’t load the application');
          return null;
        }),
    [id, show],
  );

  useEffect(() => {
    setData(null);
    setEditing(false);
    void load();
  }, [load]);

  const refresh = () => {
    void load();
    onChanged();
  };

  if (error) {
    return (
      <div style={{ padding: 24 }}>
        <div className="card" style={{ padding: 16, color: 'var(--st-declined)' }}>
          {error}
        </div>
      </div>
    );
  }
  if (!data) {
    return <EmptyState icon="clock" title="Loading…" body="Getting the application." />;
  }

  const { submission, schema } = data;
  const actionable = isActionable(submission.type, submission.stage);
  // Only the acting stage's staff fields are editable; fields an earlier stage
  // completed (e.g. CS's, when Accounts is approving) appear read-only.
  const actingStage = approverFor(submission.type, submission.stage);
  const staffSplit = actingStage
    ? stageStaffFields(schema, actingStage)
    : { editable: staffFields(schema), context: [] as FieldDef[] };
  const staff = staffSplit.editable.filter((f) => f.type !== 'heading');
  const staffContext = staffSplit.context.filter((f) => !isBlank(submission.internalData[f.id]));
  const decidedStaff = actionable ? [] : staff.filter((f) => !isBlank(submission.internalData[f.id]));
  const customerSchema = customerFields(schema);
  const customer = valueFields(customerSchema);
  const name = applicantName(data);
  const email = applicantEmail(data.schema, submission.customerData);

  // The newest iQ post. iQ can return HTTP 200 yet fail, so trust `ok`.
  const latestIq = data.iqPosts[0];
  const iqSentOk = !!latestIq && latestIq.ok && !latestIq.stubbed;
  const iqFailed = !!latestIq && !latestIq.ok && !latestIq.stubbed;
  const iqCode =
    latestIq?.code ??
    (typeof submission.internalData['_iq_code'] === 'string' ? (submission.internalData['_iq_code'] as string) : null);
  // Named approvers per stage (Settings → Approvers); the API enforces it too.
  const mayDecide = data.decision?.allowed ?? true;
  const emails = data.emails ?? null;

  const internalData = (): Record<string, unknown> => {
    const out: Record<string, unknown> = {};
    for (const f of staff) {
      const v = staffValues[f.id] ?? '';
      out[f.id] = f.type === 'number' && v !== '' ? Number(v) : v;
    }
    return out;
  };

  const doResend = async () => {
    setResending(true);
    try {
      const r = await api.resendCard(submission.id);
      toast({ title: `Card sent again to ${r.channel ?? 'Teams'}`, icon: 'teams' });
      refresh();
    } catch (e) {
      toast({ title: 'The card didn’t send', body: (e as Error).message, icon: 'shield' });
    } finally {
      setResending(false);
    }
  };

  const doResendIq = async () => {
    setResendingIq(true);
    try {
      const r = await api.resendIq(submission.id);
      const what = r.entity === 'lead' ? 'Lead' : submission.type === 'supplier_account' ? 'Supplier' : 'Customer';
      if (r.stubbed) {
        toast({ title: 'iQ isn’t connected here', body: 'Nothing was sent.', icon: 'db' });
      } else if (r.ok) {
        toast({ title: `${what}${r.code ? ` ${r.code}` : ''} ${r.op === 'update' ? 'updated' : 'created'} in iQ`, icon: 'db' });
      } else {
        const answer = iqHintAnswer(r.message);
        toast({
          title: 'iQ still couldn’t take it',
          body: `iQ said: ${r.message ?? 'nothing'}${answer ? `. Correct the ${answer} under Their answers, then send again.` : ''}`,
          icon: 'shield',
        });
      }
      refresh();
    } catch (e) {
      toast({ title: 'Couldn’t send to iQ', body: (e as Error).message, icon: 'shield' });
    } finally {
      setResendingIq(false);
    }
  };

  const startEdit = () => {
    const seed: Record<string, unknown> = {};
    for (const f of customer) {
      if (isApplicantEditable(f)) seed[f.id] = submission.customerData[f.id];
    }
    setEdits(seed);
    setEditing(true);
  };

  const doSaveEdit = async () => {
    // Send only answers that actually changed.
    const changed: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(edits)) {
      if (JSON.stringify(v ?? '') !== JSON.stringify(submission.customerData[k] ?? '')) {
        changed[k] = v;
      }
    }
    if (Object.keys(changed).length === 0) {
      setEditing(false);
      return;
    }
    setSavingEdit(true);
    try {
      await api.amendSubmission(submission.id, { customerData: changed });
      const n = Object.keys(changed).length;
      toast({ title: `Corrected ${n} ${n === 1 ? 'answer' : 'answers'}`, icon: 'check' });
      setEditing(false);
      refresh();
    } catch (e) {
      toast({ title: 'Couldn’t save the answers', body: (e as Error).message, icon: 'shield' });
    } finally {
      setSavingEdit(false);
    }
  };

  const markMissing = (ids: string[]) => {
    setMissing(new Set(ids));
    staffRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const doApprove = async () => {
    if (!actingStage) return;
    // Required questions first: nothing is sent while one is empty.
    const empty = staff.filter((f) => f.required && isBlank(staffValues[f.id]));
    if (empty.length) {
      markMissing(empty.map((f) => f.id));
      return;
    }
    setBusy(true);
    try {
      await api.decideSubmission(submission.id, { decision: 'approve', internalData: internalData() });
      const fresh = await load();
      onChanged();
      const newPost = fresh && fresh.iqPosts.length > data.iqPosts.length ? fresh.iqPosts[0]! : null;
      const t = approvedToast(
        submission.type,
        actingStage,
        newPost
          ? {
              ok: newPost.ok,
              stubbed: newPost.stubbed,
              code: newPost.code ?? (typeof fresh?.submission.internalData['_iq_code'] === 'string' ? (fresh.submission.internalData['_iq_code'] as string) : iqCode),
            }
          : null,
        emails?.approve ?? null,
      );
      toast({ title: t.title, body: t.body, icon: t.bad ? 'shield' : 'check' });
    } catch (e) {
      const ids = e instanceof ApiRequestError ? (e.details as { missing?: string[] } | undefined)?.missing : undefined;
      if (ids?.length) markMissing(ids);
      toast({ title: 'Couldn’t approve', body: (e as Error).message, icon: 'shield' });
    } finally {
      setBusy(false);
    }
  };

  const doDecline = async (reason: string) => {
    if (!actingStage) return;
    setBusy(true);
    try {
      await api.decideSubmission(submission.id, { decision: 'decline', reason });
      setDeclineOpen(false);
      const t = declinedToast(name, actingStage, emails?.decline ?? null);
      toast({ title: t.title, body: t.body, icon: 'x' });
      refresh();
    } catch (e) {
      toast({ title: 'Couldn’t decline', body: (e as Error).message, icon: 'shield' });
    } finally {
      setBusy(false);
    }
  };

  const more: MoreItem[] = [
    ...(actionable
      ? [
          {
            icon: 'send',
            label: resending ? 'Sending the card…' : 'Resend card',
            description: 'Posts the card to Teams again, for when it’s gone missing.',
            onClick: () => void doResend(),
            disabled: resending,
          },
        ]
      : []),
    {
      icon: 'db',
      label: resendingIq ? 'Sending to iQ…' : 'Send to iQ again',
      description: iqSentOk
        ? `Already in iQ${iqCode ? ` as ${iqCode}` : ''}.`
        : 'Sends the answers to iQ again, using the form as it is now.',
      onClick: () => void doResendIq(),
      disabled: resendingIq || iqSentOk,
    },
    ...(actionable
      ? [
          {
            icon: 'teams',
            label: 'See the Teams card',
            description: 'What the approvers see in Teams, as it would post now.',
            onClick: () => setCardOpen(true),
          },
        ]
      : []),
    ...(isAdmin
      ? [
          {
            icon: 'audit',
            label: 'View in audit log',
            description: 'Every change to this application, with before and after.',
            onClick: () => navigate(`/audit?entity=${encodeURIComponent(submission.id)}&range=all`),
          },
        ]
      : []),
  ];

  const decided = decidedSentence(submission.type, submission.stage, data.approvals);
  const iqDoing =
    submission.type === 'lead'
      ? 'create the lead'
      : latestIq?.action?.includes('update')
        ? 'add the credit terms'
        : submission.type === 'supplier_account'
          ? 'set the supplier up'
          : 'set the customer up';
  const decline = actingStage ? declineDialog(submission.type, actingStage, name, emails?.decline ?? null) : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'column', height: '100%' }} className="fade-in">
      <div
        style={{
          padding: '16px 22px',
          borderBottom: '1px solid var(--border)',
          display: 'flex',
          alignItems: 'center',
          gap: 12,
        }}
      >
        <Avatar name={name} size={40} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 9, flexWrap: 'wrap' }}>
            <h2
              style={{
                margin: 0,
                fontSize: 17,
                fontWeight: 800,
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {name}
            </h2>
            <ApplicationPill sub={{ ...submission, iq: latestIq ?? null, timing: data.timing }} />
          </div>
          <div style={{ fontSize: 12.5, color: 'var(--text-2)', marginTop: 1, overflowWrap: 'anywhere' }}>
            {applicationType(submission.type)} · received {timeAgo(submission.createdAt)}
            {email ? ` · ${email}` : ''} · <span className="mono">{shortId(submission.id)}</span>
            {iqCode && (
              <>
                {' · '}
                <span title="iQ code" style={{ fontWeight: 700, color: 'var(--accent-text)' }}>
                  iQ {iqCode}
                </span>
              </>
            )}
            {latestIq?.stubbed && ' · iQ isn’t connected here'}
          </div>
        </div>
        <MoreMenu items={more} />
      </div>

      <div style={{ flex: 1, padding: 22, overflowY: 'auto', overflowX: 'hidden' }}>
        <div className="card" style={{ padding: '20px 24px 16px', marginBottom: 18 }}>
          <StageStepper type={submission.type} stage={submission.stage} iqFailed={iqFailed} />
          {actionable && actingStage ? (
            <WaitingLine timing={data.timing} stage={actingStage} />
          ) : (
            decided && <div style={{ marginTop: 16, fontSize: 13, color: 'var(--text-2)' }}>{decided}</div>
          )}
        </div>

        {iqFailed && (
          <ProblemNotice
            title="Not in iQ yet."
            body={`iQ couldn’t ${iqDoing}. ${iqHint(latestIq.message)}`}
            said={latestIq.message ? `iQ said: ${latestIq.message}` : null}
            button={{ icon: 'db', label: 'Send to iQ again', busyLabel: 'Sending…' }}
            busy={resendingIq}
            onFix={() => void doResendIq()}
          />
        )}

        {actionable && data.teamsCardError && (
          <ProblemNotice
            title="The Teams card didn’t post."
            body="JFH Forms tries again every hour. You can resend it now, or decide here instead."
            said={`Teams said: ${data.teamsCardError}`}
            button={{ icon: 'send', label: 'Resend card', busyLabel: 'Sending…' }}
            busy={resending}
            onFix={() => void doResend()}
          />
        )}

        <div style={{ display: 'grid', gridTemplateColumns: '1fr', gap: 18 }}>
          {(actionable ? staff.length > 0 || staffContext.length > 0 : decidedStaff.length > 0) && (
            <div className="card" style={{ padding: 20, scrollMarginTop: 12 }} ref={staffRef}>
              <SectionLabel
                icon="bolt"
                title={actionable ? (mayDecide ? 'Before you approve' : 'For the approver to fill in') : 'Filled in by staff'}
                hint="Applicants never see these"
              />
              {actionable && staffContext.length > 0 && (
                <div style={{ marginTop: 6, marginBottom: staff.length > 0 ? 14 : 0 }}>
                  <div style={{ fontSize: 11.5, fontWeight: 700, color: 'var(--text-3)', marginBottom: 4 }}>
                    Filled in by Customer Service
                  </div>
                  {staffContext.map((f) => (
                    <FieldRow key={f.id} label={f.label} value={displayValue(submission.internalData[f.id])} />
                  ))}
                </div>
              )}
              {actionable && staff.length > 0 && (
                <StaffFields
                  fields={staff}
                  values={staffValues}
                  disabled={!mayDecide}
                  missing={missing}
                  onChange={(fid, v) => {
                    setStaffValues((prev) => ({ ...prev, [fid]: v }));
                    if (missing.has(fid)) setMissing((prev) => new Set([...prev].filter((x) => x !== fid)));
                  }}
                  countries={countries}
                  defaultCountryIso={defaultCountryIso}
                />
              )}
              {!actionable && (
                <div style={{ marginTop: 6 }}>
                  {decidedStaff.map((f) => (
                    <FieldRow key={f.id} label={f.label} value={displayValue(submission.internalData[f.id])} />
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="card" style={{ padding: 20 }}>
            <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 12 }}>
              <SectionLabel
                icon="user"
                title="Their answers"
                hint={editing ? 'Change the answers, then save' : 'What the applicant sent'}
              />
              {editing ? (
                <div style={{ display: 'flex', gap: 8, flex: 'none' }}>
                  <button className="btn sm" onClick={() => setEditing(false)} disabled={savingEdit}>
                    Cancel
                  </button>
                  <button className="btn primary sm" onClick={() => void doSaveEdit()} disabled={savingEdit}>
                    <Icon name="check" size={14} /> {savingEdit ? 'Saving…' : 'Save answers'}
                  </button>
                </div>
              ) : (
                <button className="btn sm" style={{ flex: 'none' }} onClick={startEdit}>
                  <Icon name="pencil" size={14} /> Correct an answer
                </button>
              )}
            </div>
            {editing ? (
              <ApplicantEditor
                fields={customer}
                values={edits}
                onChange={(fid, v) => setEdits((prev) => ({ ...prev, [fid]: v }))}
                countries={countries}
                defaultCountryIso={defaultCountryIso}
              />
            ) : (
              answerGroups(customerSchema).map((g, gi) => (
                <section key={gi} aria-label={g.title ?? undefined} style={{ marginTop: g.title ? 16 : 6 }}>
                  {g.title && (
                    <div
                      style={{
                        fontSize: 11,
                        fontWeight: 800,
                        letterSpacing: '.08em',
                        textTransform: 'uppercase',
                        color: 'var(--jfh-cactus)',
                        marginBottom: 2,
                      }}
                    >
                      {g.title}
                    </div>
                  )}
                  {g.fields.map((f) => {
                    const raw = submission.customerData[f.id];
                    if ((f.type === 'repeater' || f.type === 'contact') && isRepeaterValue(raw)) {
                      return <RepeaterRows key={f.id} field={f} items={raw} />;
                    }
                    if (f.type === 'company_number') {
                      return (
                        <CompanyRows
                          key={f.id}
                          label={f.label}
                          value={displayValue(raw)}
                          check={companyChecksOf(submission.customerData)[f.id]}
                        />
                      );
                    }
                    return <FieldRow key={f.id} label={f.label} value={displayValue(raw)} />;
                  })}
                </section>
              ))
            )}
          </div>

          <div className="card" style={{ padding: 20 }}>
            <SectionLabel icon="audit" title="Activity" hint="Everything that happened, in order" />
            <ActivityTrail rows={data.activity} />
          </div>
        </div>
      </div>

      {actionable && actingStage && (
        <div
          style={{
            borderTop: '1px solid var(--border)',
            background: 'var(--bg-surface)',
            padding: '12px 22px',
            display: 'flex',
            alignItems: 'center',
            gap: 14,
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: 1, minWidth: 220, fontSize: 12.5, color: 'var(--text-2)' }}>
            {mayDecide
              ? approveConsequence(submission.type, actingStage, emails?.approve ?? null)
              : data.decision!.approvers.length
                ? `Only ${joinNames(data.decision!.approvers.map((a) => a.name))} can decide this stage.`
                : `Nobody named for ${APPROVAL_STAGE_LABEL[data.decision!.stage]} can sign in at the moment.`}
          </div>
          <div style={{ display: 'flex', gap: 9 }}>
            <button className="btn danger" onClick={() => setDeclineOpen(true)} disabled={busy || !mayDecide}>
              <Icon name="x" size={16} /> Decline
            </button>
            <button className="btn primary" onClick={() => void doApprove()} disabled={busy || !mayDecide}>
              <Icon name="check" size={16} /> {busy ? 'Approving…' : approveLabel(submission.type, submission.stage)}
            </button>
          </div>
        </div>
      )}

      {decline && (
        <DeclineModal
          open={declineOpen}
          title={decline.title}
          lines={decline.lines}
          busy={busy}
          onClose={() => setDeclineOpen(false)}
          onConfirm={(reason) => void doDecline(reason)}
        />
      )}
      {actionable && <CardPreviewModal id={submission.id} open={cardOpen} onClose={() => setCardOpen(false)} />}
    </div>
  );
}

/** "Elvin Hughes and Priya Nadarajah", "A, B and C". */
function joinNames(names: string[]): string {
  return names.length < 2 ? (names[0] ?? '') : `${names.slice(0, -1).join(', ')} and ${names[names.length - 1]}`;
}

/** Best-effort applicant display name from the first short_text customer field. */
function applicantName(data: SubmissionDetailResponse): string {
  const field: FieldDef | undefined = data.schema.find(
    (f) => f.audience === 'customer' && f.type === 'short_text',
  );
  const v = field ? data.submission.customerData[field.id] : undefined;
  return typeof v === 'string' && v ? v : '(unnamed)';
}

/** One sentence under the stepper: how long it has waited, and when it's overdue. */
function WaitingLine({ timing, stage }: { timing: ApprovalTiming | null; stage: ApprovalStage }) {
  const team = teamName(stage);
  const Team = team.charAt(0).toUpperCase() + team.slice(1);
  if (!timing) {
    return <div style={{ marginTop: 16, fontSize: 13 }}><strong>Waiting for {team}.</strong></div>;
  }
  const limit = `${timing.days} working day${timing.days === 1 ? '' : 's'}`;
  return (
    <div
      style={{
        marginTop: 16,
        display: 'flex',
        gap: 9,
        alignItems: 'flex-start',
        fontSize: 13,
        color: timing.overdue ? 'var(--st-declined)' : 'var(--text-1)',
      }}
    >
      <Icon name="clock" size={15} style={{ flex: 'none', marginTop: 1, color: timing.overdue ? 'var(--st-declined)' : 'var(--text-3)' }} />
      <div>
        {timing.overdue ? (
          <>
            <strong>Overdue.</strong> Waiting for {team} since {ukDayTime(timing.waitingSince)}. The limit is {limit}.{' '}
            {timing.reminderSentAt
              ? `Reminder sent ${ukDayTime(timing.reminderSentAt)}.`
              : 'A reminder goes out in the next working hour.'}
          </>
        ) : (
          <>
            <strong>Waiting for {team}.</strong> Since {ukDayTime(timing.waitingSince)}. If it’s still waiting on{' '}
            {ukDayTime(timing.dueAt)}, {Team.replace(/^The /, 'the ')} get a reminder.
          </>
        )}
      </div>
    </div>
  );
}
